import styled from 'styled-components';
import theme from '../theme';

export const PageWrapper = styled.div`
  font-family: ${theme.typography.fontFamily};
`;

export const PageTitle = styled.h1`
  font-size: ${theme.typography.h1.size};
  font-weight: ${theme.typography.h1.weight};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const SettingsButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.radii.sm};
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primaryGhost};
  }
`;

export const FilterBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border-radius: ${theme.radii.md};
  flex-wrap: wrap;
  align-items: center;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const FilterLabel = styled.span`
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  color: ${theme.colors.textSecondary};
`;

export const FilterSelect = styled.select`
  padding: 5px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.small.size};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background: ${theme.colors.surface};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryGhost};
  }
`;

export const FilterCount = styled.div`
  margin-left: auto;
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textMuted};
`;

export const ViewToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing.md};
  gap: 0;
`;

export const ToggleButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  background: ${({ $active }) => $active ? theme.colors.primary : theme.colors.surface};
  color: ${({ $active }) => $active ? 'white' : theme.colors.textSecondary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:first-child {
    border-radius: ${theme.radii.sm} 0 0 ${theme.radii.sm};
    border-right: none;
  }

  &:last-child {
    border-radius: 0 ${theme.radii.sm} ${theme.radii.sm} 0;
  }

  &:hover {
    background: ${({ $active }) => $active ? theme.colors.primaryLight : theme.colors.surfaceHover};
  }
`;

export const ManagerGroupHeader = styled.h3`
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  color: ${theme.colors.text};
  margin: ${theme.spacing.lg} 0 ${theme.spacing.md} 0;
  padding-left: ${theme.spacing.sm};
  border-left: 3px solid ${theme.colors.primary};
`;

export const MemberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

export const MemberCard = styled.div`
  background: ${({ $bgColor }) => $bgColor || theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  border-top: 3px solid ${({ $borderColor }) => $borderColor || theme.colors.border};
  display: flex;
  flex-direction: column;
  transition: all ${theme.transitions.fast};

  &:hover {
    box-shadow: ${theme.shadows.md};
    transform: translateY(-1px);
  }
`;

export const CardHeaderRow = styled.div`
  padding: ${theme.spacing.lg} ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${theme.radii.full};
  background: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.typography.small.size};
  font-weight: 700;
  flex-shrink: 0;
`;

export const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MemberName = styled.div`
  font-weight: 600;
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MemberRole = styled.div`
  font-size: ${theme.typography.tiny.size};
  color: ${theme.colors.textSecondary};
`;

export const StatsGrid = styled.div`
  display: flex;
  justify-content: space-around;
  padding: ${theme.spacing.lg};
`;

export const StatItem = styled.div`
  text-align: center;
`;

export const StatNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $color }) => $color || theme.colors.text};
`;

export const StatLabel = styled.div`
  font-size: ${theme.typography.tiny.size};
  color: ${theme.colors.textMuted};
  margin-top: 2px;
`;

export const CardFooter = styled.button`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border: none;
  border-top: 1px solid ${theme.colors.borderLight};
  background: transparent;
  color: ${theme.colors.primary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  border-radius: 0 0 ${theme.radii.md} ${theme.radii.md};

  &:hover {
    background: ${theme.colors.primaryGhost};
  }
`;

// Table view
export const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  margin-bottom: ${theme.spacing.lg};
`;

export const TableHead = styled.thead`
  th {
    font-size: ${theme.typography.small.size};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${theme.colors.textSecondary};
    background: ${theme.colors.surfaceHover};
    border-bottom: 2px solid ${theme.colors.border};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    text-align: left;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    transition: color ${theme.transitions.fast};

    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

export const TableRow = styled.tr`
  cursor: pointer;
  background: ${({ $bgColor }) => $bgColor || 'transparent'};
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surfaceHover};
  }

  td {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    border-top: 1px solid ${theme.colors.borderLight};
    color: ${theme.colors.text};
    vertical-align: middle;
  }
`;

export const TableAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const TableAvatarCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${theme.radii.full};
  background: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
`;

export const ScoreText = styled.span`
  font-weight: 600;
  color: ${({ $color }) => $color || theme.colors.textMuted};
`;

export const SortIndicator = styled.span`
  margin-left: 4px;
  opacity: ${({ $active }) => $active ? 1 : 0};
`;

// Detail panel
export const DetailPanel = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

export const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

export const DetailTitle = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.radii.sm};
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.background};
    color: ${theme.colors.text};
  }
`;

// States
export const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.body.size};
`;

export const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.4;
`;

export const EmptyTitle = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

export const EmptyText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.body.size};
  margin: 0;
`;
